import { m } from '@hub/messages';
import { Link, useMatches } from '@hub/router';
import type { FooterVariant } from '@hub/router/definePageRoute';
import { type FlagName, useFlagSnapshot } from '@platform/feature-flags';
import {
  Footer as FooterContainer,
  FooterCopyright,
  FooterDivider,
  FooterLink,
  FooterLinks,
  FooterSocial,
  FooterSocialLink,
  SiteFooter,
  type SiteFooterLink,
  type SiteFooterSocialLink,
  SocialIcon,
} from '@platform/ui';
import { SubscriptionPaths } from '../../domains/subscription/route.paths';
import { AboutLink, CompanyLink, LegalLink, ServiceLink, SocialMediaLink } from '../commons/static-links';

type LegalLinkDef = {
  key: string;
  label: string;
  href: string;
  flag?: FlagName;
  internal?: boolean;
  target?: '_blank';
};

function getLegalLinks(): LegalLinkDef[] {
  return [
    { key: 'imprint', internal: false, label: m.commons_footer_imprint(), href: LegalLink.Imprint, target: '_blank' },
    { key: 'agb', label: m.commons_footer_agb(), href: LegalLink.AGB, target: '_blank' },
    { key: 'data-protection', label: m.commons_footer_data_protection(), href: LegalLink.DataProtection, target: '_blank' },
    { key: 'accessibility', label: m.commons_footer_accessibility(), href: LegalLink.Accessibility, target: '_blank' },
    { key: 'ce-declaration', label: m.commons_footer_ce_declaration(), href: LegalLink.CEDeclaration, target: '_blank' },
    { key: 'cancellation', label: m.commons_footer_cancellation(), href: LegalLink.Cancellation, target: '_blank' },
    {
      key: 'withdrawal',
      label: m.commons_footer_withdrawal(),
      href: SubscriptionPaths.withdrawal.path,
      flag: 'page-withdrawal',
      internal: true,
      target: '_blank',
    },
    { key: 'transparency', label: m.commons_footer_transparency(), href: LegalLink.Transparency, target: '_blank' },
    { key: 'data-act', label: m.commons_footer_data_act(), href: LegalLink.DataAct, target: '_blank' },
  ];
}

function useEnabledLegalLinks(): LegalLinkDef[] {
  const flags = useFlagSnapshot();
  return getLegalLinks().filter((link) => !link.flag || flags[link.flag] === true);
}

function toSiteFooterLink(link: LegalLinkDef): SiteFooterLink {
  if (link.internal) {
    return {
      key: link.key,
      asChild: true,
      label: (
        <Link target={link.target} to={link.href}>
          {link.label}
        </Link>
      ),
    };
  }
  return { label: link.label, href: link.href, target: link.target };
}

function getSocialLinks(): SiteFooterSocialLink[] {
  return [
    { href: SocialMediaLink.Facebook, icon: 'facebook', label: 'Facebook' },
    { href: SocialMediaLink.Instagram, icon: 'instagram', label: 'Instagram' },
    { href: SocialMediaLink.YouTube, icon: 'youtube', label: 'YouTube' },
    { href: SocialMediaLink.X, icon: 'x', label: 'X' },
  ];
}

// Reads the leaf route's footerVariant; deeper matches win over layout parents.
function useFooterVariant(): FooterVariant | undefined {
  return useMatches({ select: (matches) => matches.at(-1)?.staticData?.footerVariant });
}

function MinimalFooter({ legalLinks, className }: { legalLinks: LegalLinkDef[]; className?: string }) {
  return (
    <FooterContainer className={className}>
      <FooterDivider />
      <FooterLinks aria-label={m.commons_links_legal_links()}>
        {legalLinks
          .filter((link) => link.href !== LegalLink.Cancellation)
          .map((link) =>
            link.internal ? (
              <FooterLink key={link.key} asChild>
                <Link to={link.href}>{link.label}</Link>
              </FooterLink>
            ) : (
              <FooterLink key={link.key} href={link.href} target={link.target}>
                {link.label}
              </FooterLink>
            ),
          )}
      </FooterLinks>
      <FooterDivider />
      <FooterSocial aria-label={m.commons_links_social_media()}>
        {getSocialLinks().map((link) => (
          <FooterSocialLink key={link.href} href={link.href} aria-label={link.label}>
            <SocialIcon name={link.icon} />
          </FooterSocialLink>
        ))}
      </FooterSocial>
      <FooterDivider />
      <FooterCopyright>{m.commons_footer_copyright()}</FooterCopyright>
    </FooterContainer>
  );
}

export function Footer({ className }: { className?: string }) {
  const variant = useFooterVariant();
  const legalLinks = useEnabledLegalLinks();

  if (variant === 'minimal') {
    return <MinimalFooter legalLinks={legalLinks} className={className} />;
  }

  return (
    <SiteFooter
      className={className}
      paymentSectionTitle={m.commons_footer_section_payment()}
      sections={[
        {
          title: m.commons_footer_section_about(),
          links: [
            { label: m.commons_footer_about_offers(), href: AboutLink.Offers, target: '_blank' },
            { label: m.commons_footer_about_netflix(), href: AboutLink.Netflix, target: '_blank' },
            { label: m.commons_footer_about_options(), href: AboutLink.Options, target: '_blank' },
            { label: m.commons_footer_about_hd(), href: AboutLink.HD, target: '_blank' },
          ],
        },
        {
          title: m.commons_footer_section_service(),
          links: [
            { label: m.commons_footer_service_help(), href: ServiceLink.Help, target: '_blank' },
            { label: m.commons_footer_service_faq(), href: ServiceLink.FAQ, target: '_blank' },
            { label: m.commons_footer_service_contact(), href: ServiceLink.Contact, target: '_blank' },
          ],
        },
        {
          title: m.commons_footer_section_company(),
          links: [
            { label: m.commons_footer_company_about(), href: CompanyLink.About, target: '_blank' },
            { label: m.commons_footer_company_careers(), href: CompanyLink.Careers, target: '_blank' },
            { label: m.commons_footer_company_press(), href: CompanyLink.Press, target: '_blank' },
            { label: m.commons_footer_company_advertising(), href: CompanyLink.Advertising, target: '_blank' },
          ],
        },
      ]}
      legalLinks={legalLinks.map(toSiteFooterLink)}
      legalLinksAriaLabel={m.commons_links_legal_links()}
      socialLinks={getSocialLinks()}
      socialLinksAriaLabel={m.commons_links_social_media()}
      copyright={m.commons_footer_copyright()}
    />
  );
}
